import { useController, useFormContext, useFormState } from "react-hook-form";
import { useFieldName } from "../rhf/FieldPath";
import { useAppFormContext } from "../rhf/FormProvider";

/**
 * FieldBridge
 * -----------
 * Binds a shared input to the form (react-hook-form) and calls `render` with
 * the props the inputs use: `handler`, `touched`, `invalid`, `hasError`, ...
 * (the same shape react-reactive-form's FieldControl had, so the inputs did
 * not need rewriting). Must be rendered inside a `<FormProvider>`; the field
 * name is prefixed by any enclosing `<FieldPath>` (e.g. "address.city").
 */
const FieldBridge = ({ name, render }) => <Field name={name} render={render} />;

export default FieldBridge;

// Value an onChange call carries: a DOM/synthetic event or the raw value
const eventValue = (eventOrValue) => {
  const target = eventOrValue?.target;
  if (target && typeof target === "object" && "value" in target) {
    return target.type === "checkbox" ? target.checked : target.value;
  }
  return eventOrValue;
};

const Field = ({ name: id, render }) => {
  const name = useFieldName(id);
  const { control } = useFormContext();
  // Subscribe this field to the form's submit state: reading it from the
  // context object only re-renders the form's owner, so fields under a
  // memoised component never saw that the form was submitted
  const formState = useFormState({ control });
  const appForm = useAppFormContext();
  const { field, fieldState } = useController({ name, control });
  const { error } = fieldState;

  // With criteriaMode "all" every failing rule is listed in `error.types`
  // ({ required: true, pattern: true, ... })
  const errors = error
    ? error.types ?? { [error.type]: error.message || true }
    : null;

  const handler = (inputType) => {
    const base = {
      name: id, // DOM name stays the short id, like before
      value: field.value ?? "",
      onChange: (eventOrValue, ...args) => {
        // Only a real value change makes the form non-pristine
        // (e.g. the phone input echoes its value on load)
        if (eventValue(eventOrValue) !== field.value) {
          appForm?.markUserChanged?.(name);
        }
        field.onChange(eventOrValue, ...args);
      },
      onBlur: () => field.onBlur(),
      onFocus: () => {},
      disabled: field.disabled || !!appForm?.isFieldDisabled?.(name),
    };
    if (inputType === "checkbox" || inputType === "switch") {
      base.checked = !!field.value;
    }
    return base;
  };

  return render({
    handler,
    value: field.value,
    // A submitted form counts as touched, so every error shows after "Submit"
    touched: fieldState.isTouched || formState.isSubmitted,
    untouched: !(fieldState.isTouched || formState.isSubmitted),
    dirty: fieldState.isDirty,
    pristine: !fieldState.isDirty,
    invalid: fieldState.invalid,
    valid: !fieldState.invalid,
    pending: formState.isValidating,
    errors,
    hasError: (key) => !!errors?.[key],
    getError: (key) => errors?.[key],
  });
};
